import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Button from 'carbon-react/lib/components/button';
import Typography from 'carbon-react/lib/components/typography';

import { setupHoldingOrgLink } from '../../../workspaces/api';
import { SFA_FATAL_ERROR, SFA_LINK_OPTIONS } from '../../sfa-types';
import { SfaContentForm } from '../../components/sfa/sfa-content-form';
import { SfaLinkBusinessToOrgForm } from '../../components/sfa/sfa-link-business-to-org-form';
import { SfaLinkOrgErrorMessage } from '../../components/sfa/error-messages/sfa-link-org-error-message';
import { SfaLoading } from '../../components/sfa/sfa-loading';
import { useSfaOnboardingContext } from '../../contexts/sfa-onboarding-context';

export const SfaLinkBusinessToOrgContainer = () => {
  const {
    organisation: business,
    linkingData,
    setError,
    setFinishedOrganisation,
    setOrgIdToLink,
    workspace
  } = useSfaOnboardingContext();
  const intl = useIntl();

  const text = useMemo(
    () => ({
      cancel: intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' }),
      confirm: intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' }),
      linkingToOrg: intl.formatMessage({
        id: 'onboarding.linking_to_org',
        defaultMessage: 'Linking to organisation...'
      }),
      subtitle: intl.formatMessage(
        {
          id: 'onboarding.select_an_existing_futrli_org_to_link',
          defaultMessage: 'Select an existing Futrli organisation to link {businessName} to, or create a new one.'
        },
        {
          businessName: business?.name
        }
      ),
      title: intl.formatMessage(
        {
          id: 'onboarding.link_business_to_futrli_organisation',
          defaultMessage: 'Link {businessName} to a Futrli organisation'
        },
        {
          businessName: business?.name
        }
      )
    }),
    [business?.name, intl]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [lastError, setLastError] = useState<SFA_LINK_OPTIONS>();
  const [selectedOption, setSelectedOption] = useState<SFA_LINK_OPTIONS | ''>('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  const handleCancel = () => {
    setError(SFA_FATAL_ERROR.CANCEL_BY_USER);
  };

  const handleConfirm = async () => {
    try {
      if (selectedOption === SFA_LINK_OPTIONS.NEW) {
        // This will advance the user to the next screen
        setOrgIdToLink(SFA_LINK_OPTIONS.NEW);
      } else {
        setIsLoading(true);
        const finishedOrg = await setupHoldingOrgLink(workspace!.id, business!.id, selectedOrgId);
        setFinishedOrganisation(finishedOrg);
      }
    } catch (e) {
      const isNewError = !lastError;
      if (isNewError) {
        setLastError(selectedOption as SFA_LINK_OPTIONS);
        setIsLoading(false);
      } else {
        setError(SFA_FATAL_ERROR.ERROR_CREATING_LINK);
      }
    }
  };

  const handleSelectOption = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(ev.currentTarget.value as SFA_LINK_OPTIONS);
  };

  const handleSelectOrg = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOrgId(ev.target.value);
  };

  if (isLoading) {
    return <SfaLoading text={text.linkingToOrg} />;
  }

  return (
    <SfaContentForm
      saveButton={
        <Button
          buttonType="primary"
          type="button"
          onClick={handleConfirm}
          disabled={!selectedOption || (selectedOption === SFA_LINK_OPTIONS.EXISTING && !selectedOrgId)}
        >
          {text.confirm}
        </Button>
      }
      leftSideButtons={
        <Button buttonType="tertiary" type="button" onClick={handleCancel}>
          {text.cancel}
        </Button>
      }
      onSubmit={e => e.preventDefault()}
    >
      <div style={{ width: '560px', margin: '0 auto' }}>
        {lastError && <SfaLinkOrgErrorMessage />}

        <Typography mb={1} variant="h1">
          {text.title}
        </Typography>
        <Typography mb={3}>{text.subtitle}</Typography>

        <SfaLinkBusinessToOrgForm
          businessName={business?.name}
          onSelectOption={handleSelectOption}
          onSelectOrg={handleSelectOrg}
          linkingData={linkingData}
          selectedOption={selectedOption}
          selectedOrgId={selectedOrgId}
        />
      </div>
    </SfaContentForm>
  );
};
